import React, { useMemo, useState, useEffect, useCallback } from "react";
import axios from "axios";
import Header from "../components/Header";
import Footer from "../components/Footer";
import BookingForm from "../components/BookingForm";
import BudgetSummary from "../components/BudgetSummary";
import { calcBudget } from "../lib/pricing";
import { loadDraft, saveDraft } from "../lib/storage";
import { useToast } from "../hooks/use-toast";
import { Toaster } from "../components/ui/toaster";
import { Dialog, DialogContent } from "../components/ui/dialog";
import { CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STORAGE_KEY = "veysa_booking_draft";

const initialState = {
  group: "",
  insuranceId: "basico",
  pickup: "",
  pickupTime: "",
  ret: "",
  retTime: "",
  outOfHours: false,
  promoCode: "",
  name: "",
  email: "",
  phone: "",
  comments: "",
};

export default function Home() {
  const { toast } = useToast();
  const [state, setState] = useState(() => ({ ...initialState, ...loadDraft(STORAGE_KEY) }));
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending] = useState(false);
  const [successRef, setSuccessRef] = useState(null);

  useEffect(() => {
    saveDraft(STORAGE_KEY, state);
  }, [state]);

  const budget = useMemo(() => calcBudget(state), [state]);

  const handleSubmit = useCallback(async () => {
    if (sending || !budget.ready) return;
    setSending(true);
    const payload = {
      name: state.name,
      email: state.email,
      phone: state.phone || "",
      comments: state.comments || "",
      group: budget.vehicle.group,
      vehicleName: budget.vehicle.name,
      vehicleType: budget.vehicle.type,
      vehicleDetail: budget.vehicle.detail,
      insuranceName: budget.insurance.name,
      pickup: state.pickup,
      ret: state.ret,
      pickupTime: state.pickupTime || "",
      retTime: state.retTime || "",
      days: budget.days,
      outOfHours: state.outOfHours,
      promoCode: state.promoCode || "",
      promoLabel: budget.promo ? budget.promo.label : "",
      vehicleCost: budget.vehicleCost,
      insuranceCost: budget.insuranceCost,
      extrasCost: budget.extrasCost,
      discount: budget.discount,
      total: budget.total,
      fianza: budget.fianza,
      kmIncluded: budget.kmIncluded,
    };
    try {
      const { data } = await axios.post(`${API}/reservations`, payload);
      setSubmitted(true);
      setSuccessRef(data.reference);
      setTimeout(() => setSubmitted(false), 6000);
    } catch (error) {
      console.error("Error al enviar la solicitud de reserva:", error);
      toast({
        title: "No se pudo enviar la solicitud",
        description: "Int\u00e9ntalo de nuevo en unos minutos o cont\u00e1ctanos por tel\u00e9fono.",
        variant: "destructive",
      });
    } finally {
      setSending(false);
    }
  }, [sending, budget, state, toast]);

  return (
    <div className="min-h-screen bg-[#fdf9f8]">
      <Header />

      {/* HERO */}
      <section className="bg-[#631024] text-white">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-14 sm:py-20">
          <p className="text-[11px] font-semibold tracking-[0.24em] uppercase text-white/55">
            Presupuesto inteligente
          </p>
          <h1 className="font-display font-800 text-4xl sm:text-5xl leading-[1.05] mt-3 max-w-2xl" style={{ fontWeight: 800 }}>
            Calcula tu alquiler y reserva en minutos
          </h1>
          <p className="text-white/70 mt-4 max-w-xl leading-relaxed">
            Turismos, furgonetas y minivans en Madrid Norte. Elige vehículo,
            fechas y cobertura: el precio se actualiza al instante.
          </p>
        </div>
      </section>

      {/* MAIN */}
      <main className="max-w-6xl mx-auto px-5 sm:px-8 py-8 sm:py-10">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-6 items-start">
          <BookingForm
            state={state}
            setState={setState}
            budget={budget}
            onSubmit={handleSubmit}
            submitted={submitted}
            sending={sending}
          />
          <div className="lg:sticky lg:top-20">
            <BudgetSummary budget={budget} />
          </div>
        </div>
      </main>

      <Footer />
      <Toaster />

      <Dialog open={!!successRef} onOpenChange={(open) => !open && setSuccessRef(null)}>
        <DialogContent className="sm:max-w-md text-center p-8">
          <div className="mx-auto w-16 h-16 rounded-full bg-[#eaf6ec] flex items-center justify-center mb-2">
            <CheckCircle2 className="w-9 h-9 text-[#2e7d32]" />
          </div>
          <h2 className="font-display font-bold text-2xl text-[#2a1a1e]">
            ¡Solicitud enviada!
          </h2>
          <p className="text-base text-[#5c4a4e] leading-relaxed mt-1">
            Hemos recibido tu <strong>solicitud provisional</strong> y te hemos
            enviado un email de confirmación.
          </p>
          <div className="my-2 rounded-xl bg-[#f8ecef] px-5 py-4">
            <p className="text-sm text-[#9b8388] uppercase tracking-wide">Tu referencia</p>
            <p className="font-display font-800 text-xl text-[#631024]" style={{ fontWeight: 800 }}>
              {successRef}
            </p>
          </div>
          <p className="text-base text-[#5c4a4e] leading-relaxed">
            <strong>Importante:</strong> todavía <strong>no es la confirmación
            definitiva</strong>. Nuestro equipo revisará la disponibilidad del
            vehículo en esas fechas y te enviará un segundo email con la
            confirmación final.
          </p>
          <button
            type="button"
            onClick={() => setSuccessRef(null)}
            className="mt-3 w-full inline-flex items-center justify-center rounded-xl bg-[#631024] text-white text-base font-semibold px-5 py-3 transition-all duration-200 hover:bg-[#7a1530] active:scale-[0.99]"
          >
            Entendido
          </button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
